import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { FC, memo, useEffect, useState, MouseEvent, useMemo, useCallback } from 'react';
import ReactGA from 'react-ga4';
import StyledMenu from 'src/components/StyledMenu';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsOperationDeliveryByDeliveryIdEntity } from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningsOperationEntityWithStatsEntity } from 'src/entities/PlanningsOperationEntityWithStats.entity';
import { PlanningsSortablePositionEntity } from 'src/entities/planningsSortablePosition.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { PlanningsWorkingStatisticEntity } from 'src/entities/PlanningsWorkingStatistic.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { TransferRequestEntity } from 'src/entities/transferRequestEntity';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { OperationsWithStatusModel } from 'src/models/OperationsWithStatus.model';
import { PlanningsOperationDelivery, PlanningsOperationPlace } from 'src/models/PlanningsOperationGroup.model';
import { PositionModel } from 'src/models/PositionModel';
import arrayUtil from 'src/utils/array.util';
import { isOutsideBerthReservationTime } from 'src/utils/berthReservationTime.util';
import { datetimeUtil } from 'src/utils/datetime.util';
import { SelectedStatusVo } from 'src/vo/SelectedStatus.vo';

import { convertFromOperationsWithStatusModelToPlanningsSortablePositionEntities, findAssigningRequest } from '../functions';

import PlanningsCyclesPresenter from './PlanningsCycles.presenter';
import PlanningsTransferDialogPresenter from './PlanningsTransferDialog.presenter';
import SelectDriverMenuPresenter, { SelectDriverMenuDriverEntity } from './SelectDriverMenu.presenter';

type Props = {
  garage: GarageEntity;
  garageData?: GarageEntity[];
  truck: PlanningsTruckEntity;
  driver?: PlanningsDriverEntity;
  delivery: PlanningsDeliveryEntity;
  operationEntityWithStatsEntities: PlanningsOperationEntityWithStatsEntity[];
  selected: boolean;
  updateDisplayOrderId: (orderId: number) => void;
  deliveryEntities: PlanningsDeliveryEntity[];
  driverEntities: PlanningsDriverEntity[];
  truckEntities: PlanningsTruckEntity[];
  mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void;
  openTransferDialog: (entity: TransferRequestEntity) => void;
  isLoading: boolean;
  updateWorkingStatisticses: (entity: PlanningsWorkingStatisticEntity) => void;
  currentHistoryVersion: string | undefined;
  updateEditDriverId: (driverId: number) => void;
  firstVisitSet: Set<string>;
  beforeOperationEntityWithStatsEntities: PlanningsOperationEntityWithStatsEntity[] | null;
  expandAll: boolean;
  planningsOperationDelivery: PlanningsOperationDelivery;
  beforePlanningsOperationDelivery: PlanningsOperationDelivery;
  requestSingleAlgorithmPlanning: (deliveryId: number, orderOperationIdsForSort: number[], deleteOrderIdsFromOperations: number[], orderOperationCycleIndexes?: { [key: number]: number }) => void;
  startOn: string;
  endOn: string;
  planningsOperationDeliveryByDeliveryIdEntity: PlanningsOperationDeliveryByDeliveryIdEntity;
  selectedCycleIndexes: SelectedCycle[];
  updateSelectedCycleIndexes: (deliveryId: number, cycleIndexes: number[]) => void;
  resetEditPlaces: () => void;
  updateEditPlaces: (deliveryId: number, cycleIndex: number, places: PlanningsOperationPlace[]) => void;
  addEmptyCycle: (deliveryId: number) => void;
  removeEmptyCycle: (deliveryId: number, cycleIndex: number) => void;
  addDeliveryElementRef: (deliveryId: number, element: HTMLElement) => void;
  assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void;
  assignTruckQueue: AssignTruckEntity[];
  currentAssigning: AssignTruckEntity;
  updateEditTruckId: (truckId: number) => void;
  deleteTemporaryDelivery: (deliveryId: number) => void;
}

const PlanningsDeliveryPresenter: FC<Props> = memo((
  {
    garage,
    garageData,
    truck,
    driver,
    delivery,
    operationEntityWithStatsEntities,
    selected,
    updateDisplayOrderId,
    deliveryEntities,
    driverEntities,
    truckEntities,
    mutateDeleteOrdersOperations,
    openTransferDialog,
    isLoading,
    updateWorkingStatisticses,
    currentHistoryVersion,
    updateEditDriverId,
    firstVisitSet,
    beforeOperationEntityWithStatsEntities,
    expandAll,
    planningsOperationDelivery,
    beforePlanningsOperationDelivery,
    requestSingleAlgorithmPlanning,
    startOn,
    endOn,
    planningsOperationDeliveryByDeliveryIdEntity,
    selectedCycleIndexes,
    updateSelectedCycleIndexes,
    resetEditPlaces,
    updateEditPlaces,
    addEmptyCycle,
    removeEmptyCycle,
    addDeliveryElementRef,
    assignTruckToTemporaryDeliveryRequest,
    assignTruckQueue,
    currentAssigning,
    updateEditTruckId,
    deleteTemporaryDelivery,
  }
) => {
  const theme = useTheme();
  const { data: driverData } = useDriversQuery(startOn, { enabled: true, });

  const [operationsWithStatusModel, setOperationsWithStatusModel] = useState<OperationsWithStatusModel | undefined>(undefined);
  const [beforeOperationsWithStatusModel, setBeforeOperationsWithStatusModel] = useState<OperationsWithStatusModel | undefined>(undefined);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedOperations, setSelectedOperations] = useState<PlanningsOperationEntityWithStatsEntity[]>([]);
  const [quantityOver, setQuantityOver] = useState<boolean>(false);
  const [weightOver, setWeightOver] = useState<boolean>(false);
  const [volumeOver, setVolumeOver] = useState<boolean>(false);
  const [overtime, setOvertime] = useState<boolean>(false);
  const [outsideSpecifiedTime, setOutsideSpecifiedTime] = useState<boolean>(false);
  const [outsideBerthReservationTime, setOutsideBerthReservationTime] = useState<boolean>(false);
  const [tooltipTitle, setTooltipTitle] = useState<string[]>([]);
  const [workingStartAt, setWorkingStartAt] = useState<Date | undefined>(undefined);
  const [workingEndAt, setWorkingEndAt] = useState<Date | undefined>(undefined);
  const [isFirstUnloadOperation, setIsFirstUnloadOperation] = useState<boolean>(false);
  const [addedOrderIds, setAddedOrderIds] = useState<number[]>([]);
  const [prevAddedOrderIds, setPrevAddedOrderIds] = useState<number[]>([]);
  const [checkedOperationAdded, setCheckedOperationAdded] = useState<boolean>(false);
  const [transferDialogIsOpen, setTransferDialogIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<SelectedStatusVo>('none');
  const [assigningIdAndName, setAssigningIdAndName] = useState<{ id: number, name: string }>(null);

  const addSelectedOperations = useCallback((operations: PlanningsOperationEntityWithStatsEntity[]) => {
    setSelectedOperations(
      [
        ...selectedOperations.filter((it) => !operations.map(({ id }) => id).includes(it.id)),
        ...operations
      ]
    );
  }, [selectedOperations]);

  const removeSelectedOperations = useCallback((operations: PlanningsOperationEntityWithStatsEntity[]) => {
    setSelectedOperations(
      [
        ...selectedOperations.filter((it) => !operations.map(({ id }) => id).includes(it.id)),
      ]
    );
  }, [selectedOperations]);

  const selectedOrderIds = arrayUtil.uniq<number>(selectedOperations.map((it) => it.orderId));

  const checkBoxOnClick = useCallback((event: React.MouseEvent) => {
    if (!delivery) return;

    event.stopPropagation();
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `ドライバーチェック ${selected ? 'OFF' : 'ON'}`, label: delivery.driverId });

    if (selected) {
      updateSelectedCycleIndexes(delivery.id, []);
    } else {
      updateSelectedCycleIndexes(delivery.id, planningsOperationDelivery.cycles.map((cycle) => cycle.cycleIndex));
    }
  }, [delivery, planningsOperationDelivery, selected, updateSelectedCycleIndexes]);

  const expandButtonOnClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `ドライバー展開 ${isExpanded ? '閉じる' : '開く'}` });
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    setPrevAddedOrderIds(addedOrderIds);
  }, [addedOrderIds]);

  useEffect(() => {
    if (!operationsWithStatusModel) {
      setWeightOver(false);
      return;
    }

    setWeightOver(
      truck.maximumLoadingCapacityWeightForCalculation < operationsWithStatusModel.maximumLoadingWeightG()
    );
  }, [operationsWithStatusModel, truck]);

  useEffect(() => {
    const maximumLoadingCapacityQuantity = delivery.legRunEntity?.maximumLoadingCapacityQuantity;
    if (!operationsWithStatusModel || maximumLoadingCapacityQuantity == null) {
      setQuantityOver(false);
      return;
    }

    setQuantityOver(
      maximumLoadingCapacityQuantity < operationsWithStatusModel.maximumLoadingQuantity()
    );
  }, [delivery, operationsWithStatusModel]);

  useEffect(() => {
    if (!operationsWithStatusModel) {
      setVolumeOver(false);
      return;
    }

    setVolumeOver(
      truck.loadingPlatformVolumeMm3 < operationsWithStatusModel.maximumLoadingVolumeMm3()
    );
  }, [operationsWithStatusModel, truck]);

  useEffect(() => {
    if (![workingStartAt, workingEndAt].every((maybe) => maybe)) {
      setOvertime(false);
      return;
    }

    if (!operationEntityWithStatsEntities.length) {
      setOvertime(false);
      return;
    }

    const startAt = new Date(delivery.startAt);
    const endAt = new Date(delivery.endAt);
    const workingSeconds = datetimeUtil.diffSeconds(workingStartAt, workingEndAt);
    const nullDurationSeconds = delivery.operations.filter((it) => it.nullDurationSeconds > 0).reduce((prev, it) => prev + it.nullDurationSeconds, 0);
    if (delivery?.workingAvailableDurationSeconds && (workingSeconds - nullDurationSeconds) > delivery.workingAvailableDurationSeconds) {
      setOvertime(true);
      return;
    }

    setOvertime(
      (workingStartAt < startAt) || (endAt < workingEndAt)
    );
  }, [
    workingStartAt,
    workingEndAt,
    operationEntityWithStatsEntities,
    delivery
  ]);

  useEffect(() => {
    setTooltipTitle(
      [
        overtime ? '勤務時間が超過しています。' : '',
        quantityOver ? '数量が超過しています。' : '',
        weightOver ? '重量が超過しています。' : '',
        volumeOver ? '体積が超過しています。' : '',
        outsideSpecifiedTime ? '指定時間が超過しています。' : '',
        outsideBerthReservationTime ? 'バース予約の時間外です。' : '',
      ].filter((maybe) => maybe)
    );
  }, [overtime, quantityOver, weightOver, volumeOver, outsideSpecifiedTime, outsideBerthReservationTime]);

  useEffect(() => {
    setSelectedOperations([]);
  }, [isLoading]);

  useEffect(() => {
    if (!(operationEntityWithStatsEntities || planningsOperationDelivery)) {
      setOperationsWithStatusModel(undefined);
      return;
    }

    setOperationsWithStatusModel(
      new OperationsWithStatusModel(operationEntityWithStatsEntities, planningsOperationDelivery)
    );
  }, [operationEntityWithStatsEntities, planningsOperationDelivery]);

  useEffect(() => {
    if (!(beforeOperationEntityWithStatsEntities || beforePlanningsOperationDelivery)) {
      setBeforeOperationsWithStatusModel(undefined);
      return;
    }

    setBeforeOperationsWithStatusModel(
      new OperationsWithStatusModel(beforeOperationEntityWithStatsEntities, beforePlanningsOperationDelivery)
    );
  }, [beforeOperationEntityWithStatsEntities, beforePlanningsOperationDelivery]);

  useEffect(() => {
    if (!delivery) return;
    if (!planningsOperationDelivery) return;

    let minArrivalAt: Date = null;
    let maxDepartureAt: Date = null;
    if (delivery.operations.length > 0) {
      minArrivalAt = new Date(Math.min(...delivery.operations.map((it) => new Date(it.arrivalAt).getTime())));
      maxDepartureAt = new Date(Math.max(...delivery.operations.map((it) => new Date(it.departureAt).getTime())));
    }
    if (delivery.workStartAt || minArrivalAt) {
      setWorkingStartAt(
        new Date(delivery.workStartAt || minArrivalAt)
      );
    }
    if (delivery.workEndAt || maxDepartureAt) {
      setWorkingEndAt(
        new Date(delivery.workEndAt || maxDepartureAt)
      );
    }
    if ([delivery.workStartAt, delivery.workEndAt, operationEntityWithStatsEntities.length].every((maybe) => maybe)) {
      let wMinutes = 0;
      if (delivery.workStartAt && delivery.workEndAt) {
        wMinutes = datetimeUtil.diffMinutes(new Date(delivery.workStartAt), new Date(delivery.workEndAt));
      } else {
        const startAt = delivery.operations.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0].arrivalAt;
        const endAt = delivery.operations.sort((a, b,) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime())[0].departureAt;
        wMinutes = datetimeUtil.diffMinutes(new Date(startAt), new Date(endAt));
      }
      const pMinutes = delivery.workingAvailableDurationSeconds && delivery.workingAvailableDurationSeconds > 0
        ? delivery.workingAvailableDurationSeconds / 60
        : datetimeUtil.diffMinutes(new Date(delivery.startAt), new Date(delivery.endAt));

      updateWorkingStatisticses({
        deliveryId: delivery.id,
        operationCount: operationEntityWithStatsEntities.length,
        workMinutes: wMinutes,
        plannedMinutes: pMinutes,
      });
    } else {
      updateWorkingStatisticses({
        deliveryId: delivery.id,
        operationCount: 0,
        workMinutes: 0,
        plannedMinutes: 0,
      });
    }
  }, [delivery, planningsOperationDelivery, operationEntityWithStatsEntities, updateWorkingStatisticses]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const onClickMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const onClickClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const onClickEditDriver = useCallback((event: MouseEvent) => {
    if (!driver) return;
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: 'ドライバー編集' });

    event.stopPropagation();
    updateEditDriverId(driver.id);
    setAnchorEl(null);
  }, [driver, updateEditDriverId]);

  const onClickEditTruck = useCallback((event: MouseEvent) => {
    if (!truck) return;
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: 'トラック編集' });

    event.stopPropagation();
    updateEditTruckId(truck.id);
    setAnchorEl(null);
  }, [truck, updateEditTruckId]);

  const onClickDeleteTemporaryDelivery = useCallback((event: MouseEvent) => {
    if (!delivery) return;
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '仮車両削除' });

    event.stopPropagation();
    deleteTemporaryDelivery(delivery.id);
    setAnchorEl(null);
  }, [deleteTemporaryDelivery, delivery]);

  const driverMenu = useMemo(() => (
    <StyledMenu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={onClickClose}
      PaperProps={{
        style: { padding: 0, margin: 0, }
      }}
    >
      <MenuItem
        onClick={onClickEditTruck}
        disableRipple
      >
        車両編集
      </MenuItem>
      <MenuItem
        onClick={onClickEditDriver}
        disableRipple
      >
        ドライバー編集
      </MenuItem>
      {truck.temporary && (
        <MenuItem
          onClick={onClickDeleteTemporaryDelivery}
          disableRipple
        >
          仮車両削除
        </MenuItem>
      )}
    </StyledMenu>
  ), [anchorEl, menuOpen, onClickEditDriver, truck, onClickDeleteTemporaryDelivery]);

  const positions: PlanningsSortablePositionEntity[] = useMemo(() => (
    operationsWithStatusModel
      ? convertFromOperationsWithStatusModelToPlanningsSortablePositionEntities(
        operationsWithStatusModel.groupByIdx(),
        delivery.id
      )
      : []
    ), [delivery.id, operationsWithStatusModel]);

  const beforePositions: PlanningsSortablePositionEntity[] = useMemo(() => (
    beforeOperationsWithStatusModel
      ? convertFromOperationsWithStatusModelToPlanningsSortablePositionEntities(
        beforeOperationsWithStatusModel.groupByIdx(),
        delivery.id
      )
      : []
    ), [delivery.id, beforeOperationsWithStatusModel]);

  useEffect(() => {
    const currentOrderIds = positions.flatMap((it) => it.operationsWithStats).map((it) => it.orderId);
    const beforeOrderIds = beforePositions.flatMap((it) => it.operationsWithStats).map((it) => it.orderId);

    if (beforeOperationEntityWithStatsEntities === null) {
      setAddedOrderIds([]);

      return;
    }

    const added = currentOrderIds.filter((it) => !beforeOrderIds.includes(it));

    setAddedOrderIds(added);
  }, [positions, beforePositions, beforeOperationEntityWithStatsEntities]);

  useEffect(() => {
    const isAddedOrderIdsIncludesPrev = addedOrderIds.every((it) => prevAddedOrderIds.includes(it));
    const isPrevAddedOrderIdsIncludesAdded = prevAddedOrderIds.every((it) => addedOrderIds.includes(it));

    if (isAddedOrderIdsIncludesPrev && isPrevAddedOrderIdsIncludesAdded) return;

    setCheckedOperationAdded(
      !!addedOrderIds.length
    );
  }, [addedOrderIds, prevAddedOrderIds]);

  useEffect(() => {
    if (!delivery) return;
    if (!operationsWithStatusModel) return;

    if (!firstVisitSet || firstVisitSet.size === 0) {
      setIsFirstUnloadOperation(false);
      return;
    }

    // shiftTruckId は delivery.id ではなく operation の shiftTruckId（同一 delivery 内は単一）。
    // positions の `position` は Number 変換済みで API 応答の文字列（"35.10" 等）と一致しないため、
    // operationsWithStats の降 operation の raw latitude / longitude（文字列）で key を作る
    // （個別地点側 PlanningsPlace の `place.latitude / place.longitude` と同じ表現に揃える）。
    setIsFirstUnloadOperation(
      positions.some((pos) => {
        const unloadOp = pos.operationsWithStats.find(({ action }) => action === '降');
        if (!unloadOp || unloadOp.shiftTruckId == null || unloadOp.latitude == null || unloadOp.longitude == null) return false;
        // Component.tsx 側の firstVisitSet が Number 正規化済みのため同じ正規化で lookup する。
        const lat = Number(unloadOp.latitude);
        const lng = Number(unloadOp.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
        return firstVisitSet.has(`${unloadOp.shiftTruckId}|${lat}|${lng}`);
      })
    );
  }, [firstVisitSet, positions]);

  useEffect(() => {
    setOutsideSpecifiedTime(false);
    positions.forEach((pos) => {
      pos.operationsWithStats.forEach((op) => {
        if (new PositionModel(op).isOutsideSpecifiedTime()) {
          setOutsideSpecifiedTime(true);
        }
      });
    });
  }, [positions]);

  useEffect(() => {
    setOutsideBerthReservationTime(false);
    positions.forEach((pos) => {
      if (isOutsideBerthReservationTime(
        pos.operationsWithStats,
        pos.arrivalAt.toISOString(),
        pos.departureAt.toISOString(),
      )) {
        setOutsideBerthReservationTime(true);
      }
    });
  }, [positions]);

  const localSelectedCycleIndexes = useMemo(() => selectedCycleIndexes.find((it) => it.deliveryId === delivery.id)?.cycleIndexes || [], [delivery.id, selectedCycleIndexes]);

  useEffect(() => {
    if (!planningsOperationDelivery) return;

    if (localSelectedCycleIndexes.length === 0) {
      setSelectedStatus('none');
    } else if (localSelectedCycleIndexes.length === planningsOperationDelivery.cycles.length) {
      setSelectedStatus('every');
    } else {
      setSelectedStatus('some');
    }
  }, [localSelectedCycleIndexes.length, planningsOperationDelivery]);

  const updateLocalSelectedCycleIndexes = useCallback((idx: number) => {
    if (localSelectedCycleIndexes.includes(idx)) {
      updateSelectedCycleIndexes(delivery.id, localSelectedCycleIndexes.filter((it) => it !== idx));
      return;
    }
    updateSelectedCycleIndexes(delivery.id, [...localSelectedCycleIndexes, idx]);
  }, [delivery.id, localSelectedCycleIndexes, updateSelectedCycleIndexes]);

  const planningsCyclesPresenterMemo = useMemo(() => {
    if (!planningsOperationDelivery) return null;

    // 自分以外のデリバリーがあるかどうか
    const canTransfer = !!deliveryEntities.find((it) => it.id === delivery.id);
    return (
      <PlanningsCyclesPresenter
        deliveryId={delivery.id}
        driverId={delivery.driverId}
        garage={garage}
        garageData={garageData}
        truck={truck}
        planningsOperationDelivery={planningsOperationDelivery}
        firstVisitSet={firstVisitSet}
        requestSingleAlgorithmPlanning={requestSingleAlgorithmPlanning}
        selectedCycleIndexes={localSelectedCycleIndexes}
        updateSelectedCycleIndexes={updateLocalSelectedCycleIndexes}
        mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
        setTransferDialogIsOpen={setTransferDialogIsOpen}
        canTransfer={canTransfer}
        addSelectedOperations={addSelectedOperations}
        removeSelectedOperations={removeSelectedOperations}
        startOn={startOn}
        endOn={endOn}
        updateDisplayOrderId={updateDisplayOrderId}
        openTransferDialog={openTransferDialog}
        isLoading={isLoading}
        deliveryEntities={deliveryEntities}
        driverEntities={driverEntities}
        truckEntities={truckEntities}
        addEmptyCycle={addEmptyCycle}
        removeEmptyCycle={removeEmptyCycle}
        deliveryIsExpanded={isExpanded}
        resetEditPlaces={resetEditPlaces}
        updateEditPlaces={updateEditPlaces}
        planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
        addedOrderIds={addedOrderIds}
        parentCheckedOperationAdded={checkedOperationAdded}
      />
    );
  }, [planningsOperationDelivery, deliveryEntities, delivery.id, delivery.driverId, garage, garageData, truck, firstVisitSet, requestSingleAlgorithmPlanning, localSelectedCycleIndexes, updateLocalSelectedCycleIndexes, mutateDeleteOrdersOperations, addSelectedOperations, removeSelectedOperations, startOn, endOn, updateDisplayOrderId, openTransferDialog, isLoading, driverEntities, truckEntities, addEmptyCycle, removeEmptyCycle, isExpanded, resetEditPlaces, updateEditPlaces, planningsOperationDeliveryByDeliveryIdEntity, addedOrderIds, checkedOperationAdded]);

  const fixed: boolean = useMemo(() => delivery?.fixed || false, [delivery]);

  const positionWarningMemo = useMemo(() => (
    <Stack direction="row">
      {!!tooltipTitle.length && (
        <Tooltip title={tooltipTitle.join('')}>
          <WarningAmberRoundedIcon color="error" />
        </Tooltip>
      )}
      {
        isFirstUnloadOperation && (
          <Tooltip title="ドライバーが初めて訪問する納品先があります">
            <NotificationImportantIcon color="success" />
          </Tooltip>
        )
      }
      {fixed && <Chip color="info" size="small" label="確定" />}
      {operationsWithStatusModel?.orderCount() === 0 && (
        <Chip color="error" size="small" label="計画無" />
      )}
    </Stack>
  ), [tooltipTitle, isFirstUnloadOperation, fixed, operationsWithStatusModel]);

  const [driverAnchorEl, setDriverAnchorEl] = useState<null | HTMLElement>(null);
  const [driverIdsHasDelivery, setDriverIdsHasDelivery] = useState<number[]>([]);

  const handleDriverMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDriverAnchorEl(event.currentTarget);
  };

  const handleDriverListClose = useCallback((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDriverAnchorEl(null);
  }, []);

  const assignRunnings = useMemo(() => (
    [...assignTruckQueue, currentAssigning].filter((it) => it)
  ), [assignTruckQueue, currentAssigning]);
  const assigning = useMemo(() => findAssigningRequest(assignRunnings, driver, delivery.id), [assignRunnings, driver, delivery.id]);

  useEffect(() => {
    if (!(currentAssigning && assigningIdAndName && driver)) return;

    if ((assigningIdAndName?.id === -1 && driver.temporary) || (assigningIdAndName?.id > 0 && assigningIdAndName.name === driver.name)) {
      setAssigningIdAndName(null);
    }
  }, [driver, currentAssigning, assigningIdAndName]);

  const handleDriverClick = useCallback((event: React.MouseEvent, id: number, name: string) => {
    event.preventDefault();
    event.stopPropagation();

    setAssigningIdAndName({ id, name });
    assignTruckToTemporaryDeliveryRequest({
      deliveryIds: [delivery.id],
      truckId: null,
      origTruckId: null,
      licensePlateValue: null,
      driverId: id,
      driverName: name,
      origDriverId: driver?.id ?? null,
      // 割当が失敗（例: 勤務計画はすでに存在します）したら楽観表示を元の値に戻す
      onError: () => setAssigningIdAndName(null),
    });
    setDriverAnchorEl(null);
  }, [assignTruckToTemporaryDeliveryRequest, delivery, driver]);

  useEffect(() => {
    setDriverIdsHasDelivery(deliveryEntities.map((it) => it.driverId));
  }, [deliveryEntities]);

  const selectDriverMenuMemo = useMemo(() => (
    <SelectDriverMenuPresenter
      anchorEl={driverAnchorEl}
      onClose={handleDriverListClose}
      onDriverClick={handleDriverClick}
      driverData={driverData as SelectDriverMenuDriverEntity[]}
      driverIdsHasDelivery={driverIdsHasDelivery}
      currentDriverId={driver?.id ?? null}
      isTemporaryDriver={!!driver?.temporary}
    />
  ), [driver, driverAnchorEl, driverData, driverIdsHasDelivery, handleDriverClick, handleDriverListClose]);

  // 粗利計算
  const grossProfitInfo = useMemo(() => {
    if (!delivery?.legRunEntity?.shipmentOrderLegRuns) {
      return { grossProfit: 0, totalBilling: 0, totalPayment: 0 };
    }

    const entities = delivery.legRunEntity.shipmentOrderLegRuns;
    // 各サイクルの各作業から注文IDを取得して集計
    const billingByLegRunId = new Map<number, number>();
    const paymentByLegRunId = new Map<number, number>();

    entities.forEach((entity) => {
      if (entity.shipment_order_billing_amount) {
        const { shipment_order_leg_run_id, total_amount } = entity.shipment_order_billing_amount;
        if (!billingByLegRunId.has(shipment_order_leg_run_id)) {
          billingByLegRunId.set(shipment_order_leg_run_id, total_amount);
        }
      }
      if (entity.shipment_order_leg_run_payment_amount) {
        const { shipment_order_leg_run_id, total_amount } = entity.shipment_order_leg_run_payment_amount;
        if (!paymentByLegRunId.has(shipment_order_leg_run_id)) {
          paymentByLegRunId.set(shipment_order_leg_run_id, total_amount);
        }
      }
    })

    let totalBilling = 0;
    billingByLegRunId.forEach((amount) => { totalBilling += amount; });

    let totalPayment = 0;
    paymentByLegRunId.forEach((amount) => { totalPayment += amount; });

    const grossProfit = totalBilling - totalPayment;

    return { grossProfit, totalBilling, totalPayment };
  }, [delivery]);

  const titleLineMemo = useMemo(() => {
    const time = [
      datetimeDecorator.toDayAndHourMinutes(new Date(delivery.startAt)),
      datetimeDecorator.toDayAndHourMinutes(new Date(delivery.endAt)),
    ].join(' - ');
    const duration = delivery.workingAvailableDurationSeconds && delivery.workingAvailableDurationSeconds > 0 ? `, ${numberDecorator.convertToHoursAndMinutes(delivery.workingAvailableDurationSeconds / 60)}` : '';
    const timeText = `(${time}${duration})`;

    return (
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={0.5}
        sx={{
          '&:hover': {
            backgroundColor: 'grey.200',
          },
          '&:hover .hover-icons': {
            display: 'flex',
          },
          '&:hover .hover-icons-padding': {
            display: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            width: '100%',
            mr: 4,
          }}
          alignItems="flex-start"
          justifyContent="space-between"
          className="driver-title"
        >
          <Stack direction="row">
            <Typography
              variant="h5"
              onClick={checkBoxOnClick}
              style={{
                cursor: 'pointer',
              }}
            >
              <span style={{ textDecoration: (!driver || driver.isActive) ? '' : 'line-through' }}>
                <Button
                  onClick={handleDriverMenuOpen}
                  disabled={!!assigning || fixed}
                  sx={{ p: 0, m: 0, }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: (assigningIdAndName ? assigningIdAndName.id === -1 : driver?.temporary) ? '#E65100' : '' }}
                  >
                    {assigningIdAndName?.name ?? (driver?.temporary ? '指定なし' : (driver?.name ?? '未割当'))}
                  </Typography>
                  <ArrowDropDownIcon />
                </Button>
                <span
                  style={{
                    paddingLeft: 5,
                    fontSize: '95%',
                    marginRight: '4px',
                  }}
                >
                  {timeText}
                </span>
              </span>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Tooltip
                  title={
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body2">
                        請求: ¥{numberDecorator.formatNumber(grossProfitInfo.totalBilling)}
                      </Typography>
                      <Typography variant="body2">
                        支払: ¥{numberDecorator.formatNumber(grossProfitInfo.totalPayment)}
                      </Typography>
                    </Stack>
                  }
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{
                      color: 'text.secondary',
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
                <Typography variant="body2">
                  粗利: ¥{numberDecorator.formatNumber(grossProfitInfo.grossProfit)}
                </Typography>
              </Stack>
            </Typography>
          </Stack>
          {positionWarningMemo}
        </Stack>
        {selectDriverMenuMemo}

        <IconButton
          onClick={onClickMenu}
          sx={{
            padding: 0.5,
            margin: 0,
            display: 'none',
            position: 'absolute',
            right: 8,
            zIndex: 100,
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#fff',
            },
          }}
          disabled={!!currentHistoryVersion}
          className="hover-icons"
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>
    );
  }, [checkBoxOnClick, delivery, driver, onClickMenu, currentHistoryVersion, selectDriverMenuMemo, assigningIdAndName, assigning, positionWarningMemo, fixed]);

  useEffect(() => {
    setIsExpanded(expandAll);
  }, [expandAll]);

  const expandPositionsMemo = useMemo(() => (
    <IconButton
      onClick={expandButtonOnClick}
      size="small"
    >
      {isExpanded
        ? <KeyboardArrowUpRoundedIcon fontSize="small" />
        : <ExpandMoreRoundedIcon fontSize="small" />}
    </IconButton>
  ), [expandButtonOnClick, isExpanded]);

  const totalNullDurationSeconds = useMemo(() =>
    delivery.operations
      .filter((it) => it.nullDurationSeconds > 0)
      .reduce((prev, it) => prev + it.nullDurationSeconds, 0),
    [delivery.operations]
  );

  const statisticsMemo = useMemo(() => {
    if (!planningsOperationDelivery || !truck || planningsOperationDelivery.empty) return null;

    const unloadCount = ['納品先', planningsOperationDelivery.unloadPlaceCount].join(' ');
    const itemCount = ['数量', numberDecorator.formatNumber(planningsOperationDelivery.itemCount)].join(' ');
    const quantityRatio = delivery.legRunEntity?.maximumLoadingCapacityQuantity
      ? ['数量', numberDecorator.toRoundedRate(planningsOperationDelivery.maxQuantity / delivery.legRunEntity.maximumLoadingCapacityQuantity, '')].join('')
      : null;
    const weightRatio = ['重量', numberDecorator.toRoundedRate(planningsOperationDelivery.maxWeightG / truck.maximumLoadingCapacityWeightForCalculation, '')].join('');
    const volumeRatio = (planningsOperationDelivery.maxVolumeMm3 && truck.loadingPlatformVolumeMm3)
      ? ['体積', numberDecorator.toRoundedRate(planningsOperationDelivery.maxVolumeMm3 / truck.loadingPlatformVolumeMm3, '')].join('')
      : null;
    const ratio = ['積載:', quantityRatio, weightRatio, volumeRatio].filter((it) => it).join(' ');

    let wMinutes = 0;
    if ([delivery.workStartAt, delivery.workEndAt].every((it) => it)) {
      wMinutes = datetimeUtil.diffMinutes(new Date(delivery.workStartAt), new Date(delivery.workEndAt));
    } else if (delivery.operations.length > 0) {
      const startAt = delivery.operations.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0].arrivalAt;
      const endAt = delivery.operations.sort((a, b) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime())[0].departureAt;
      wMinutes = datetimeUtil.diffMinutes(new Date(startAt), new Date(endAt));
    }
    wMinutes = wMinutes - (totalNullDurationSeconds / 60);

    const diffHours = datetimeUtil.diffHours(new Date(delivery.startAt), new Date(delivery.endAt));
    const days = Math.ceil(diffHours / 24); // 開始時間から起算して何日分の稼働か(日付の数ではない)
    const pMinutes = delivery.workingAvailableDurationSeconds && delivery.workingAvailableDurationSeconds > 0
      ? (delivery.workingAvailableDurationSeconds / 60) * days
      : datetimeUtil.diffMinutes(new Date(delivery.startAt), new Date(delivery.endAt));
    const workRatio = ['稼働', numberDecorator.toRoundedRate(wMinutes / pMinutes, '')].join(' ');
    const stats = [unloadCount, itemCount, ratio, workRatio].join('・');

    return (
      <Stack
        alignItems="center"
        direction="row"
        gap={0.5}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: '0.9em' }}
        >
          {stats}
        </Typography>
      </Stack>
    );
  }, [delivery, planningsOperationDelivery, truck, totalNullDurationSeconds]);

  const operationTimes = useMemo(() => {
    if (!planningsOperationDelivery || planningsOperationDelivery.empty) return '';

    let text = '配車前(まだ計画がありません)';
    if (!planningsOperationDelivery.empty && workingStartAt && workingEndAt) {
      const isSameDay = datetimeUtil.isSameDay(workingStartAt, workingEndAt);
      const times = isSameDay ? [
          datetimeDecorator.toHourMinutes(workingStartAt),
          datetimeDecorator.toHourMinutes(workingEndAt),
        ].join(' ~ ')
        : [
          `${datetimeDecorator.toMmDd(workingStartAt)} ${datetimeDecorator.toHourMinutes(workingStartAt)}`,
          `${datetimeDecorator.toMmDd(workingEndAt)} ${datetimeDecorator.toHourMinutes(workingEndAt)}`
        ].join(' ~ ');
      const duration = [
          '(',
          numberDecorator.convertToHoursAndMinutes(
            datetimeUtil.diffMinutes(
              workingStartAt,
              workingEndAt
            ) - (totalNullDurationSeconds / 60)
          ),
          totalNullDurationSeconds > 0 ? ` ※アイドル時間${Math.ceil(totalNullDurationSeconds / 60)}分を除外` : '',
          ')',
        ].join('');
      text = ['計画', times, duration].join(' ');
    }

    return (
      <Stack direction="column">
        <Divider sx={{ my: 0.5 }} />
        <Box
          sx={{
            backgroundColor: 'grey.300',
            padding: '4px 8px',
            mr: '8px',
            borderRadius: '16px',
            width: 'fit-content',
          }}
        >
          <Typography variant="body2" color="text.primary">
            {text}
          </Typography>
        </Box>
      </Stack>
    );
  }, [workingEndAt, workingStartAt, planningsOperationDelivery, totalNullDurationSeconds]);

  const deliveryBgColor = () => {
    if (checkedOperationAdded) return '#EAEFF6';

    return '';
  };

  return (
    <Stack
      borderLeft={8}
      borderColor={driver ? (driver.color || theme.colors.success.light) : '#BDBDBD'}
      spacing={0.5}
      // ref={(el) => { addDeliveryElementRef(delivery.id, el); }}
    >
      {driverMenu}
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{ backgroundColor: deliveryBgColor() }}
      >
        {expandPositionsMemo}
        <Checkbox
          size="small"
          sx={{
            m: 0,
            p: 0.3,
          }}
          onClick={checkBoxOnClick}
          checked={selectedStatus === 'every'}
          indeterminate={selectedStatus === 'some'}
          disabled={isLoading}
        />
        <Stack
          flexGrow={1}
        >
          <Stack
            alignItems="flex-start"
            justifyContent="space-between"
            direction="row"
            gap={0.5}
            marginRight={1}
          >
            {titleLineMemo}
          </Stack>
          {statisticsMemo}
          {isExpanded && operationTimes}
          {isExpanded && planningsCyclesPresenterMemo}
        </Stack>
        {transferDialogIsOpen && (
          <PlanningsTransferDialogPresenter
            transferDialogIsOpen={transferDialogIsOpen}
            setTransferDialogIsOpen={setTransferDialogIsOpen}
            fromDeliveryId={delivery.id}
            deliveryEntities={deliveryEntities}
            driverEntities={driverEntities}
            truckEntities={truckEntities}
            openTransferDialog={openTransferDialog}
            selectedOrderIds={selectedOrderIds}
            planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
          />
        )}
      </Stack>
    </Stack>
  );
});

export default PlanningsDeliveryPresenter;
